import React from "react";
import api from "@/lib/api";
import {
  TrendingUp,
  TrendingDown,
  PlusSquare,
  MinusSquare,
  ArrowUpRight,
  ArrowDownRight,
  ArrowRight,
  Loader2,
  GitCompareArrows,
  ChevronDown,
} from "lucide-react";

const fmtUSD = (v) =>
  typeof v === "number"
    ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(v)
    : v;

const fmtPct = (p) =>
  typeof p === "number"
    ? `${p > 0 ? "+" : ""}${p.toFixed(2)}%`
    : "—";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" });
  } catch (_e) {
    return iso;
  }
};

const SECTIONS = [
  {
    key: "ups",
    label: "Hausses de prix",
    short: "Hausses",
    icon: TrendingUp,
    text: "text-rose-700",
    iconBg: "bg-rose-100",
    bar: "from-rose-500 to-rose-300",
    cardBorder: "border-rose-200",
    cardBg: "bg-gradient-to-br from-rose-50 via-white to-rose-50/30",
    activeRing: "ring-2 ring-rose-400",
  },
  {
    key: "downs",
    label: "Baisses de prix",
    short: "Baisses",
    icon: TrendingDown,
    text: "text-emerald-700",
    iconBg: "bg-emerald-100",
    bar: "from-emerald-500 to-emerald-300",
    cardBorder: "border-emerald-200",
    cardBg: "bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30",
    activeRing: "ring-2 ring-emerald-400",
  },
  {
    key: "added",
    label: "Nouvelles références",
    short: "Nouveautés",
    icon: PlusSquare,
    text: "text-sky-700",
    iconBg: "bg-sky-100",
    bar: "from-sky-500 to-sky-300",
    cardBorder: "border-sky-200",
    cardBg: "bg-gradient-to-br from-sky-50 via-white to-sky-50/30",
    activeRing: "ring-2 ring-sky-400",
  },
  {
    key: "removed",
    label: "Références retirées",
    short: "Retirées",
    icon: MinusSquare,
    text: "text-zinc-700",
    iconBg: "bg-zinc-200",
    bar: "from-zinc-500 to-zinc-300",
    cardBorder: "border-zinc-300",
    cardBg: "bg-gradient-to-br from-zinc-50 via-white to-zinc-50/40",
    activeRing: "ring-2 ring-zinc-500",
  },
];

export default function PricelistDiffPanel() {
  const [diff, setDiff] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  // Single-active-section pattern: only one detail table is visible at a time
  // (or none at all). Click a card to expand, click again to collapse.
  const [activeSection, setActiveSection] = React.useState(null);
  const toggle = (k) => setActiveSection((cur) => (cur === k ? null : k));

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      // limit=5000 = the server-side cap. Keeps the detail tables exhaustive
      // (every change visible) while the cards display the authoritative
      // total counts via the `summary` block.
      const r = await api.get("/pricelist/base/last-diff?limit=5000");
      setDiff(r.data);
    } catch (_e) {
      setDiff({ available: false });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading) {
    return (
      <div className="border border-zinc-200 rounded-sm p-6 bg-white flex items-center gap-3 text-sm text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Calcul du delta tarifaire…
      </div>
    );
  }

  if (!diff || diff.available === false) {
    return (
      <div className="border border-zinc-200 rounded-sm p-6 bg-white text-sm text-zinc-500" data-testid="pricelist-diff-empty">
        Aucun delta tarifaire détecté — uploadez une nouvelle Liste de prix pour générer la comparaison automatique avec l'édition précédente.
      </div>
    );
  }

  const s = diff.summary || {};
  // Hausses : tri ascendant par % de variation pour ne pas choquer le
  // partenaire — il voit d'abord les hausses modérées, et seulement en
  // déroulant la liste les plus importantes.
  const ups = (diff.changed || [])
    .filter((c) => c.direction === "up")
    .slice()
    .sort((a, b) => (a.delta_pct ?? 0) - (b.delta_pct ?? 0));
  const downs = (diff.changed || []).filter((c) => c.direction === "down");
  const added = diff.added || [];
  const removed = diff.removed || [];

  const lists = { ups, downs, added, removed };

  return (
    <div className="border border-zinc-200 rounded-md bg-white overflow-hidden shadow-sm" data-testid="pricelist-diff-panel">
      {/* Hero comparison header — « édition précédente → nouvelle » */}
      <div className="relative bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 text-white">
        {/* Decorative top rainbow bar */}
        <div className="h-1 bg-gradient-to-r from-zinc-500 via-[#D42B1E] to-[#D42B1E]" />
        <div className="px-5 sm:px-6 py-4 sm:py-5">
          {/* Title row */}
          <div className="flex items-start gap-3 flex-wrap">
            <div className="h-10 w-10 rounded-sm bg-[#D42B1E]/15 border border-[#D42B1E]/30 flex items-center justify-center shrink-0">
              <GitCompareArrows className="h-5 w-5 text-[#FF6B6B]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#FF6B6B]">
                Évolution tarifaire automatique
              </p>
              <h3 className="mt-1 text-lg sm:text-xl font-bold tracking-tight">
                Édition précédente → nouvelle
              </h3>
              <p className="mt-1 text-[11px] text-zinc-400 font-mono">
                Comparaison calculée le {fmtDate(diff.created_at)}
              </p>
            </div>
            {/* Global references delta badge */}
            {(() => {
              const prev = diff.prev_row_count || 0;
              const next = diff.new_row_count || 0;
              const delta = next - prev;
              const isPos = delta > 0;
              const isNeg = delta < 0;
              return (
                <div className={[
                  "shrink-0 rounded-sm px-3 py-2 text-right border",
                  isPos ? "bg-emerald-500/10 border-emerald-400/40 text-emerald-300"
                    : isNeg ? "bg-rose-500/10 border-rose-400/40 text-rose-300"
                    : "bg-zinc-500/10 border-zinc-400/30 text-zinc-300",
                ].join(" ")}>
                  <p className="text-[9px] font-semibold tracking-[0.16em] uppercase opacity-80">
                    Δ Références
                  </p>
                  <p className="mt-0.5 font-mono text-lg font-bold">
                    {delta > 0 ? "+" : ""}{delta.toLocaleString("fr-FR")}
                  </p>
                </div>
              );
            })()}
          </div>
        </div>

        {/* From → To visual row — previous (left) | arrow | new (right) */}
        <div className="px-5 sm:px-6 pb-5 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 md:gap-4 items-stretch">
          {/* Previous edition */}
          <div className="rounded-sm bg-white/5 border border-white/10 p-4 backdrop-blur-sm" data-testid="diff-previous-edition">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[9px] font-semibold tracking-[0.2em] uppercase text-zinc-400">
                Édition précédente
              </span>
              <span className="text-[10px] font-mono text-zinc-500 bg-white/5 px-1.5 py-0.5 rounded-sm">
                {diff.previous_effective_date || "n/d"}
              </span>
            </div>
            <p className="mt-2 font-mono text-[11px] text-zinc-200 break-all leading-snug line-clamp-2" title={diff.previous_filename || ""}>
              {diff.previous_filename || "—"}
            </p>
            <div className="mt-3 pt-3 border-t border-white/10 flex items-baseline gap-1.5">
              <span className="font-mono text-2xl font-bold text-white tabular-nums">
                {(diff.prev_row_count || 0).toLocaleString("fr-FR")}
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                références
              </span>
            </div>
          </div>

          {/* Central arrow — vertical stack on mobile, horizontal on md+ */}
          <div className="flex md:flex-col items-center justify-center gap-2 py-1">
            <div className="h-px md:h-12 w-12 md:w-px bg-gradient-to-r md:bg-gradient-to-b from-transparent via-[#D42B1E] to-transparent" />
            <div className="h-8 w-8 rounded-full bg-[#D42B1E] flex items-center justify-center shadow-lg shadow-[#D42B1E]/30 animate-pulse">
              <ArrowRight className="h-4 w-4 text-white md:hidden" />
              <ArrowDownRight className="h-4 w-4 text-white hidden md:block" />
            </div>
            <div className="h-px md:h-12 w-12 md:w-px bg-gradient-to-r md:bg-gradient-to-b from-[#D42B1E] via-[#D42B1E] to-transparent" />
          </div>

          {/* New edition — highlighted with red accent */}
          <div className="rounded-sm bg-[#D42B1E]/15 border border-[#D42B1E]/40 p-4 relative overflow-hidden" data-testid="diff-new-edition">
            {/* Subtle corner ribbon */}
            <div className="absolute -top-6 -right-6 h-16 w-16 bg-[#D42B1E]/20 rotate-45" />
            <div className="relative flex items-center justify-between gap-2">
              <span className="text-[9px] font-semibold tracking-[0.2em] uppercase text-[#FF6B6B]">
                Nouvelle édition
              </span>
              <span className="text-[10px] font-mono text-white bg-[#D42B1E]/60 px-1.5 py-0.5 rounded-sm font-semibold">
                {diff.new_effective_date || "n/d"}
              </span>
            </div>
            <p className="relative mt-2 font-mono text-[11px] text-white break-all leading-snug line-clamp-2" title={diff.new_filename || ""}>
              {diff.new_filename || "—"}
            </p>
            <div className="relative mt-3 pt-3 border-t border-[#D42B1E]/30 flex items-baseline gap-1.5">
              <span className="font-mono text-2xl font-bold text-white tabular-nums">
                {(diff.new_row_count || 0).toLocaleString("fr-FR")}
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[#FF6B6B]">
                références
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Highlights */}
      {(s.biggest_up || s.biggest_down) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-100">
          {s.biggest_up && (
            <Highlight kind="up" row={s.biggest_up} testId="diff-highlight-biggest-up" />
          )}
          {s.biggest_down && (
            <Highlight kind="down" row={s.biggest_down} testId="diff-highlight-biggest-down" />
          )}
        </div>
      )}

      {/* Aesthetic card grid — one card per category. Click a card to
          expand its detail table below. Each card has its own gradient
          accent bar, count, mini icon and short label. */}
      <div className="px-5 sm:px-6 py-5 border-t border-zinc-200 bg-zinc-50/40">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pricelist-diff-cards">
          {SECTIONS.map((section) => {
            const rows = lists[section.key] || [];
            // Authoritative counts come from the server-side summary (full
            // delta over the entire pricelist, not the truncated 200-row
            // preview rows[]). Fall back to rows.length only when the
            // backend hasn't returned a summary yet (unlikely).
            const totalCount =
              section.key === "ups" ? (s.ups ?? rows.length)
              : section.key === "downs" ? (s.downs ?? rows.length)
              : section.key === "added" ? (s.added ?? rows.length)
              : section.key === "removed" ? (s.removed ?? rows.length)
              : rows.length;
            const previewCount = rows.length;
            const isTruncated = previewCount > 0 && previewCount < totalCount;
            const Icon = section.icon;
            const isActive = activeSection === section.key;
            return (
              <button
                key={section.key}
                type="button"
                onClick={() => toggle(section.key)}
                aria-expanded={isActive}
                data-testid={`diff-section-toggle-${section.key}`}
                className={[
                  "group relative text-left rounded-md border overflow-hidden transition-all duration-200",
                  "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zinc-700",
                  section.cardBorder,
                  section.cardBg,
                  isActive
                    ? `${section.activeRing} ring-offset-2 shadow-md scale-[1.015]`
                    : "hover:shadow-md hover:-translate-y-0.5",
                ].join(" ")}
              >
                {/* Gradient accent bar */}
                <div className={`h-1 bg-gradient-to-r ${section.bar}`} />
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`h-9 w-9 rounded-full inline-flex items-center justify-center ${section.iconBg} ${section.text}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-zinc-400 transition-transform ${isActive ? "rotate-180 text-zinc-700" : "group-hover:translate-y-0.5"}`}
                    />
                  </div>
                  <p className={`mt-3 text-3xl font-bold tabular-nums tracking-tight ${section.text}`} data-testid={`diff-card-count-${section.key}`}>
                    {totalCount.toLocaleString("fr-FR")}
                  </p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
                    {section.label}
                  </p>
                  {isTruncated && (
                    <p className="mt-1 text-[10px] text-zinc-400 italic">
                      Aperçu : {previewCount.toLocaleString("fr-FR")} les + significatives
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Detail panel — only rendered when a card is active */}
        {activeSection && (
          <div className="mt-4 border border-zinc-200 bg-white rounded-md overflow-hidden" data-testid={`diff-section-table-${activeSection}`}>
            <ActiveDetail
              section={SECTIONS.find((s) => s.key === activeSection)}
              rows={lists[activeSection] || []}
              onClose={() => setActiveSection(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function Highlight({ kind, row, testId }) {
  const isUp = kind === "up";
  return (
    <div className={"bg-white p-4 " + (isUp ? "border-l-2 border-rose-500" : "border-l-2 border-emerald-500")} data-testid={testId}>
      <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-zinc-500">
        {isUp ? "Plus forte hausse" : "Plus forte baisse"}
      </div>
      <div className="mt-1 font-mono text-xs text-zinc-600">{row.sku}</div>
      <div className="text-sm text-zinc-900 truncate" title={row.name}>{row.name}</div>
      <div className="mt-2 flex items-center gap-2">
        <span className="text-xs font-mono text-zinc-500 line-through">{fmtUSD(row.old_price)}</span>
        <span className="text-sm font-mono font-semibold text-zinc-900">{fmtUSD(row.new_price)}</span>
        <span
          className={
            "ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-bold " +
            (isUp ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700")
          }
        >
          {isUp ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {fmtPct(row.delta_pct)}
        </span>
      </div>
    </div>
  );
}


function ActiveDetail({ section, rows, onClose }) {
  const Icon = section.icon;
  const isPriceChange = section.key === "ups" || section.key === "downs";
  return (
    <>
      {/* Coloured header strip */}
      <div className={`h-1 bg-gradient-to-r ${section.bar}`} />
      <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-3 flex-wrap">
        <span className={`h-8 w-8 rounded-full inline-flex items-center justify-center ${section.iconBg} ${section.text}`}>
          <Icon className="h-4 w-4" />
        </span>
        <h4 className="text-sm font-semibold text-zinc-900 tracking-tight">{section.label}</h4>
        <span className="text-[10px] font-mono text-zinc-500 px-1.5 py-0.5 rounded-sm bg-zinc-100">
          {rows.length.toLocaleString("fr-FR")}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto text-[11px] text-zinc-500 hover:text-[#D42B1E] inline-flex items-center gap-1"
          data-testid={`diff-section-close-${section.key}`}
        >
          Fermer
        </button>
      </div>
      <div className="overflow-x-auto max-h-[440px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-zinc-50 border-b border-zinc-200 z-10">
            <tr className="text-[11px] uppercase tracking-wider text-zinc-500">
              <th className="px-4 py-2.5 text-left font-semibold">Référence</th>
              <th className="px-4 py-2.5 text-left font-semibold">Produit</th>
              <th className="px-4 py-2.5 text-left font-semibold">Famille</th>
              {isPriceChange ? (
                <>
                  <th className="px-4 py-2.5 text-right font-semibold">Ancien</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Nouveau</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Δ</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Variation</th>
                </>
              ) : (
                <th className="px-4 py-2.5 text-right font-semibold">Prix</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.length === 0 && (
              <tr>
                <td className="px-4 py-8 text-center text-zinc-400 text-sm" colSpan={7}>
                  Aucun élément.
                </td>
              </tr>
            )}
            {rows.map((r, idx) => (
              <tr key={`${r.sku}-${idx}`} className="hover:bg-zinc-50/60 transition-colors" data-testid={`diff-row-${r.sku}`}>
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-700 whitespace-nowrap">{r.sku}</td>
                <td className="px-4 py-2.5 text-zinc-800 max-w-[420px] truncate" title={r.name}>{r.name || "—"}</td>
                <td className="px-4 py-2.5 text-xs text-zinc-500 whitespace-nowrap">{r.family || "—"}</td>
                {isPriceChange ? (
                  <>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-zinc-500">{fmtUSD(r.old_price)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-sm text-zinc-900">{fmtUSD(r.new_price)}</td>
                    <td className={"px-4 py-2.5 text-right font-mono text-xs " + (r.direction === "up" ? "text-rose-700" : "text-emerald-700")}>
                      {fmtUSD(r.delta)}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={
                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-bold " +
                        (r.direction === "up" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700")
                      }>
                        {r.direction === "up" ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                        {fmtPct(r.delta_pct)}
                      </span>
                    </td>
                  </>
                ) : (
                  <td className="px-4 py-2.5 text-right font-mono text-sm text-zinc-900">{fmtUSD(r.price)}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
